"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(101);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const mountedRef = useRef(true);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      mountedRef.current = false;
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const [isCreateDropOpen, setIsCreateDropOpen] = useState(false);
  const [isMemesCreateOpen, setIsMemesCreateOpen] = useState(false);
  const [isCurationDropModalOpen, setIsCurationDropModalOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[7] !== isCurationWave || $[8] !== isLoggedIn || $[9] !== participation || $[10] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[7] = isCurationWave;
    $[8] = isLoggedIn;
    $[9] = participation;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    canCreateDrop
  } = t6;
  const showToggleableDropInput = submissionExperience !== WaveSubmissionExperience.MEMES_LEGACY && submissionExperience !== WaveSubmissionExperience.CURATION_LEGACY && submissionExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL && isCreateDropOpen;
  let t7;
  if ($[12] !== canCreateDrop || $[13] !== submissionExperience) {
    t7 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.MEMES_LEGACY) {
        setIsMemesCreateOpen(true);
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY) {
        if (!canCreateDrop) {
          return;
        }
        setIsCurationDropModalOpen(true);
        return;
      }
      if (submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL) {
        if (!canCreateDrop) {
          return;
        }
        setIsCreateDropOpen(true);
        return;
      }
      setIsCreateDropOpen(true);
    };
    $[12] = canCreateDrop;
    $[13] = submissionExperience;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const onCreateDrop = t7;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t8;
  if ($[15] !== isCurationWave) {
    t8 = value => value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
    $[15] = isCurationWave;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t8);
  const t9 = wave.wave.type !== ApiWaveType.Chat;
  let t10;
  if ($[17] !== t9 || $[18] !== wave.id) {
    t10 = {
      waveId: wave.id,
      enabled: t9
    };
    $[17] = t9;
    $[18] = wave.id;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const {
    data: t11,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurations(t10);
  let t12;
  if ($[20] !== t11) {
    t12 = t11 === undefined ? [] : t11;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const curationGroups = t12;
  let t13;
  if ($[22] !== searchParams) {
    t13 = searchParams.get("curation_id");
    $[22] = searchParams;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const rawCuratedByGroupId = t13;
  const curationGroupIdSet = new Set(curationGroups.map(_temp));
  let t14;
  bb0: {
    if (!rawCuratedByGroupId) {
      t14 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t14 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t14 = rawCuratedByGroupId;
      break bb0;
    }
    t14 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t14;
  let t15;
  bb1: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || sort === WaveDropsLeaderboardSort.PRICE)) {
      t15 = "ETH";
      break bb1;
    }
    t15 = undefined;
  }
  const priceCurrency = t15;
  let t16;
  if ($[24] !== pathname || $[25] !== router || $[26] !== searchParams) {
    t16 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curation_id", groupId);
      } else {
        nextParams.delete("curation_id");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[24] = pathname;
    $[25] = router;
    $[26] = searchParams;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const updateCurationGroupInUrl = t16;
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = t18 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t18;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const updatePriceRange = t17;
  let t18;
  bb2: {
    if (!isMemesWave) {
      t18 = viewMode;
      break bb2;
    }
    if (viewMode === "grid_content_only") {
      t18 = "grid";
      break bb2;
    }
    t18 = viewMode;
  }
  const effectiveViewMode = t18;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    let t19;
    if ($[29] !== curatedByGroupId || $[30] !== maxPrice || $[31] !== minPrice || $[32] !== onCreateDrop || $[33] !== priceCurrency || $[34] !== sort || $[35] !== wave) {
      t19 = <WaveLeaderboardDrops wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={onCreateDrop} />;
      $[29] = curatedByGroupId;
      $[30] = maxPrice;
      $[31] = minPrice;
      $[32] = onCreateDrop;
      $[33] = priceCurrency;
      $[34] = sort;
      $[35] = wave;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    leaderboardContent = t19;
  } else {
    if (!isMemesWave) {
      const t19 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t20;
      if ($[37] !== curatedByGroupId || $[38] !== maxPrice || $[39] !== minPrice || $[40] !== onDropClick || $[41] !== priceCurrency || $[42] !== sort || $[43] !== t19 || $[44] !== wave) {
        t20 = <WaveLeaderboardGrid wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t19} onDropClick={onDropClick} />;
        $[37] = curatedByGroupId;
        $[38] = maxPrice;
        $[39] = minPrice;
        $[40] = onDropClick;
        $[41] = priceCurrency;
        $[42] = sort;
        $[43] = t19;
        $[44] = wave;
        $[45] = t20;
      } else {
        t20 = $[45];
      }
      leaderboardContent = t20;
    } else {
      let t19;
      if ($[46] !== curatedByGroupId || $[47] !== maxPrice || $[48] !== minPrice || $[49] !== onDropClick || $[50] !== priceCurrency || $[51] !== sort || $[52] !== wave) {
        t19 = <WaveLeaderboardGallery wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
        $[46] = curatedByGroupId;
        $[47] = maxPrice;
        $[48] = minPrice;
        $[49] = onDropClick;
        $[50] = priceCurrency;
        $[51] = sort;
        $[52] = wave;
        $[53] = t19;
      } else {
        t19 = $[53];
      }
      leaderboardContent = t19;
    }
  }
  let t19;
  if ($[54] !== wave) {
    t19 = <WaveLeaderboardTime wave={wave} />;
    $[54] = wave;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== setViewMode) {
    t20 = mode => setViewMode(mode);
    $[56] = setViewMode;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== setSort) {
    t21 = s => setSort(s);
    $[58] = setSort;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  const t22 = curatedByGroupId ?? null;
  const t23 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  const t24 = isCurationWave ? updatePriceRange : undefined;
  let t25;
  if ($[60] !== curationGroups || $[61] !== effectiveViewMode || $[62] !== maxPrice || $[63] !== minPrice || $[64] !== onCreateDrop || $[65] !== sort || $[66] !== t20 || $[67] !== t21 || $[68] !== t22 || $[69] !== t23 || $[70] !== t24 || $[71] !== wave) {
    t25 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t20} onCreateDrop={onCreateDrop} onSortChange={t21} curationGroups={curationGroups} curatedByGroupId={t22} onCurationGroupChange={t23} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t24} /></div>;
    $[60] = curationGroups;
    $[61] = effectiveViewMode;
    $[62] = maxPrice;
    $[63] = minPrice;
    $[64] = onCreateDrop;
    $[65] = sort;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
    $[71] = wave;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== showToggleableDropInput || $[74] !== wave) {
    t26 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} /></motion.div>;
    $[73] = showToggleableDropInput;
    $[74] = wave;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t26) {
    t27 = <AnimatePresence>{t26}</AnimatePresence>;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== isMemesCreateOpen || $[79] !== submissionExperience || $[80] !== wave) {
    t28 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && isMemesCreateOpen && <MemesArtSubmissionModal isOpen={isMemesCreateOpen} wave={wave} onClose={() => setIsMemesCreateOpen(false)} />;
    $[78] = isMemesCreateOpen;
    $[79] = submissionExperience;
    $[80] = wave;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  let t29;
  if ($[82] !== isCurationDropModalOpen || $[83] !== submissionExperience || $[84] !== wave) {
    t29 = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isCurationDropModalOpen && <WaveLeaderboardCurationDropModal isOpen={isCurationDropModalOpen} wave={wave} onClose={() => setIsCurationDropModalOpen(false)} />;
    $[82] = isCurationDropModalOpen;
    $[83] = submissionExperience;
    $[84] = wave;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== isCreateDropOpen || $[87] !== submissionExperience || $[88] !== wave) {
    t30 = submissionExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && isCreateDropOpen && <WaveDropCreate wave={wave} onCancel={() => setIsCreateDropOpen(false)} onSuccess={() => setIsCreateDropOpen(false)} />;
    $[86] = isCreateDropOpen;
    $[87] = submissionExperience;
    $[88] = wave;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] !== leaderboardContent || $[91] !== t27 || $[92] !== t28 || $[93] !== t29 || $[94] !== t30) {
    t31 = <div className="tw-min-w-0 tw-pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">{t27}{t28}{t29}{t30}{leaderboardContent}</div>;
    $[90] = leaderboardContent;
    $[91] = t27;
    $[92] = t28;
    $[93] = t29;
    $[94] = t30;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  let t32;
  if ($[96] !== leaderboardViewStyle || $[97] !== t19 || $[98] !== t25 || $[99] !== t31) {
    t32 = <div className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t19}{t25}{t31}</div>;
    $[96] = leaderboardViewStyle;
    $[97] = t19;
    $[98] = t25;
    $[99] = t31;
    $[100] = t32;
  } else {
    t32 = $[100];
  }
  return t32;
};
export default MyStreamWaveLeaderboard;
function _temp(group) {
  return group.id;
}